// A time-trial session on one track: car placement, surfaces, barriers,
// auto-reset, active-aero zones and lap/sector timing with track limits.

import { param } from '../config/params';
import { Car, type CarEnv, type CarInput } from './car';
import type { Track, TrackProjection } from '../track/track';
import type { PitLane } from '../track/pitLane';

export type Surface = 'track' | 'kerb' | 'runoff-asphalt' | 'grass' | 'gravel' | 'outside';

export interface SurfaceInfo {
  grip: number;
  drag: number;
}

export const SURFACES: Record<Surface, SurfaceInfo> = {
  track: { grip: 1, drag: 0 },
  kerb: { grip: 0.9, drag: 0.01 },
  'runoff-asphalt': { grip: 0.95, drag: 0 },
  grass: { grip: 0.55, drag: 0.12 },
  gravel: { grip: 0.45, drag: 0.6 },
  outside: { grip: 0.5, drag: 0.3 },
};

export interface TrackLayout {
  track: Track;
  /** Surface at a projected position; tracks without detailed geometry can use `simpleSurface`. */
  surfaceAt(p: TrackProjection): Surface;
  /** Distance from the centreline to the barrier on each side, metres. */
  barrierOffset(p: TrackProjection): { right: number; left: number };
  /** Active-aero activation zones as [startS, endS] pairs (may wrap past the line). */
  aeroZones: Array<[number, number]>;
  /** Sector boundaries as distances along the lap (excluding 0). */
  sectorEnds: number[];
  /** Distance before the line where a flying lap starts, metres. */
  flyingStartDistance: number;
  /** Pit lane (races with pit stops). */
  pitLane?: PitLane;
  /** Race only: sectors where MGU-K deployment is capped at 250 kW below 310 km/h (C5.2.8 iii "Alt 1"). */
  raceDeploySectors?: Array<[number, number]>;
}

export const sessionParams = {
  trackLimitMargin: param({ id: 'session.limitMargin', label: 'Track limit margin (m)', group: 'Session', min: 0, max: 1.5, step: 0.05, value: 0.3 }),
  resetAfter: param({ id: 'session.resetAfter', label: 'Auto-reset after (s)', group: 'Session', min: 0.5, max: 5, step: 0.1, value: 1.2 }),
  flyingSpeedKmh: param({ id: 'session.flyingKmh', label: 'Flying start speed (km/h)', group: 'Session', min: 50, max: 330, step: 5, value: 230 }),
};

/** Car half-width used for "all four wheels" track-limit checks (C2.3.1: max 1.9 m wide). */
const HALF_WIDTH = 0.95;

export function simpleSurface(p: TrackProjection): Surface {
  const edge = p.offset >= 0 ? p.widthRight : p.widthLeft;
  const d = Math.abs(p.offset) - edge;
  if (d <= 0) return 'track';
  if (d <= 1.2) return 'kerb';
  if (d <= 40) return 'grass';
  return 'outside';
}

export interface LapEvent {
  kind: 'lap' | 'sector' | 'invalid';
  lapTime?: number;
  sectorIndex?: number;
  sectorTime?: number;
  valid?: boolean;
  /** Sector times of a completed lap. */
  sectors?: Array<number | undefined>;
}

export class Session {
  readonly car = new Car();
  projection: TrackProjection;
  surface: Surface = 'track';

  /** Current lap time, or undefined before crossing the line for the first time. */
  lapTime: number | undefined;
  lapValid = true;
  lapNumber = 0;
  bestLap: number | undefined;
  lastLap: number | undefined;
  sectorTimes: Array<number | undefined> = [];
  bestSectors: Array<number | undefined> = [];
  private sectorIndex = 0;
  private sectorStart = 0;
  private stuckTime = 0;
  private prevS = 0;
  readonly events: LapEvent[] = [];
  resets = 0;
  /** Speed lost to a barrier impact in the last step, m/s (0 if none). */
  impact = 0;
  /** Side of the last impact in the car's frame: +1 barrier on the right, −1 left. */
  impactSide = 0;
  /** Surface under the left and right wheels (force feedback: one-sided kerb strikes). */
  surfaceLeft: Surface = 'track';
  surfaceRight: Surface = 'track';
  /**
   * Wheel drivers recover a spin themselves (they have reverse): only reset a
   * car that's truly stuck off track, and give it longer.
   */
  lenientReset = false;
  /** Lets a race adjust the car's environment each step (power limits, wet-weather aero). */
  envHook: ((env: CarEnv) => void) | undefined;

  constructor(readonly layout: TrackLayout) {
    this.projection = layout.track.project(0, 0);
    this.flyingStart();
  }

  flyingStart(speedKmh = sessionParams.flyingSpeedKmh.value): void {
    const s0 = this.layout.track.length - this.layout.flyingStartDistance;
    this.placeAt(s0, speedKmh / 3.6);
    this.car.pu.standingStart = false;
    this.lapTime = undefined;
    this.lapValid = true;
    this.sectorIndex = 0;
    this.sectorTimes = [];
    this.car.pu.charge = 4e6;
    this.car.pu.resetLap();
  }

  /** Standing start: the car stopped on the grid, `behind` metres before the line. */
  gridStart(behind: number): void {
    this.placeAt(this.layout.track.length - behind, 0);
    // C5.2.12 MGU-K hold-off: sim mode only, so tilt races keep their calibrated energy use.
    this.car.pu.standingStart = this.car.simMode;
    this.lapTime = undefined;
    this.lapValid = true;
    this.lapNumber = 0;
    this.sectorIndex = 0;
    this.sectorTimes = [];
    this.car.pu.charge = 4e6;
    this.car.pu.resetLap();
  }

  /** Start somewhere other than the flying-lap position (dev/testing). */
  startAt(s: number, speedKmh: number): void {
    this.placeAt(s, speedKmh / 3.6);
    this.lapTime = undefined;
  }

  private placeAt(s: number, speed: number): void {
    const f = this.layout.track.frameAt(s);
    this.car.place(f.x, f.z, f.heading, speed);
    // Manual gearboxes: first on the grid, the right gear for a flying start or reset.
    this.car.gearbox.matchSpeed(speed);
    this.projection = this.layout.track.project(f.x, f.z);
    this.prevS = this.projection.s;
    this.stuckTime = 0;
  }

  /** Put the car back on the centreline near where it left the track. */
  reset(): void {
    this.resets++;
    this.placeAt(this.projection.s, 15);
    this.invalidate();
  }

  /** Invalidate the current lap (e.g. the driving mode changed mid-lap). */
  invalidateLap(): void {
    this.invalidate();
  }

  private invalidate(): void {
    if (this.lapTime !== undefined && this.lapValid) {
      this.lapValid = false;
      this.events.push({ kind: 'invalid' });
    }
  }

  /** Restore a saved personal best. */
  setBest(lapTime: number, sectors: Array<number | undefined>): void {
    this.bestLap = lapTime;
    this.bestSectors = [...sectors];
  }

  get speed(): number {
    return this.car.speed;
  }

  step(input: CarInput, dt: number): void {
    const { track } = this.layout;
    const env: CarEnv = {
      ...SURFACES[this.surface],
      aeroZone: inZones(this.projection.s, this.layout.aeroZones, track.length),
      overtake: false,
      grade: track.elevation ? this.gradeAlongCar() : 0,
    };
    if (this.car.simMode) {
      // Sim mode: each side's wheels on their own surface. Two wheels on the grass halve
      // that side's grip and drag it back, yawing the car towards the grass.
      const left = SURFACES[this.surfaceLeft];
      const right = SURFACES[this.surfaceRight];
      env.grip = (left.grip + right.grip) / 2;
      env.drag = (left.drag + right.drag) / 2;
      env.sideDrag = right.drag - left.drag;
    }
    this.envHook?.(env);
    this.impact = 0;
    this.car.step(input, env, dt);
    if (this.lapTime !== undefined) this.lapTime += dt;

    this.projection = track.project(this.car.x, this.car.z, this.projection.index);
    this.surface = this.layout.surfaceAt(this.projection);
    this.sampleSides();
    this.handleBarriers();
    this.checkTrackLimits();
    this.checkLine(dt);
    this.checkStuck(dt);
  }

  /**
   * Move the car along a scripted path (pit lane): no physics, barriers or
   * track limits, but lap timing and line crossings carry on as normal.
   */
  followScripted(x: number, z: number, heading: number, speed: number, dt: number): void {
    const car = this.car;
    car.place(x, z, heading, speed);
    car.aero.update(false, 0, 1, dt);
    // Manual gearboxes come out of the pit lane in a gear that suits the speed.
    car.gearbox.matchSpeed(speed);
    car.gearbox.update(speed, dt);
    car.gripUseFront = car.gripUseRear = 0;
    car.mguK = 0;
    this.impact = 0;
    if (this.lapTime !== undefined) this.lapTime += dt;
    this.projection = this.layout.track.project(x, z, this.projection.index);
    this.surface = this.surfaceLeft = this.surfaceRight = 'track';
    this.stuckTime = 0;
    this.checkLine(dt);
  }

  /** Surfaces under the left and right wheels (half-track ~0.6 m), allowing for the car's angle to the track. */
  private sampleSides(): void {
    const p = this.projection;
    const f = this.layout.track.frameAt(p.s);
    // The car's right-hand direction (cos h, −sin h) projected on the track's right normal (tz, −tx).
    const right = Math.cos(this.car.heading) * f.tz + Math.sin(this.car.heading) * f.tx;
    const lateral = 0.6 * (Number.isFinite(right) ? right : 1);
    this.surfaceRight = this.layout.surfaceAt({ ...p, offset: p.offset + lateral });
    this.surfaceLeft = this.layout.surfaceAt({ ...p, offset: p.offset - lateral });
  }

  /** Track gradient projected onto the car's heading (uphill positive when driving forwards). */
  private gradeAlongCar(): number {
    const f = this.layout.track.frameAt(this.projection.s);
    const alignment = Math.sin(this.car.heading) * f.tx + Math.cos(this.car.heading) * f.tz;
    return f.slope * alignment;
  }

  private handleBarriers(): void {
    const p = this.projection;
    const barrier = this.layout.barrierOffset(p);
    const limit = p.offset >= 0 ? barrier.right - HALF_WIDTH : -(barrier.left - HALF_WIDTH);
    if (Math.abs(p.offset) <= Math.abs(limit)) return;
    // Push back inside and remove velocity into the wall.
    const f = this.layout.track.frameAt(p.s);
    const nx = f.tz * Math.sign(p.offset);
    const nz = -f.tx * Math.sign(p.offset);
    const over = Math.abs(p.offset) - Math.abs(limit);
    this.car.x -= nx * over;
    this.car.z -= nz * over;
    const before = this.car.speed;
    this.car.removeVelocityAlong(nx, nz);
    this.impact = Math.max(0, before - this.car.speed);
    // Wall normal in the car's frame (x forward, y right).
    const h = this.car.heading;
    this.impactSide = Math.sign(nx * Math.cos(h) - nz * Math.sin(h)) || 1;
  }

  private checkTrackLimits(): void {
    const p = this.projection;
    const edge = p.offset >= 0 ? p.widthRight : p.widthLeft;
    // All four wheels beyond the white line (kerbs are not track, B1.8.6).
    if (Math.abs(p.offset) - HALF_WIDTH > edge + sessionParams.trackLimitMargin.value) this.invalidate();
  }

  private checkLine(dt: number): void {
    const L = this.layout.track.length;
    const s = this.projection.s;
    const crossedLine = this.prevS > L * 0.75 && s < L * 0.25;
    const crossedBackwards = this.prevS < L * 0.25 && s > L * 0.75;
    if (crossedLine) {
      // Interpolate the crossing time within this step for accurate laps.
      const before = L - this.prevS;
      const total = before + s;
      const frac = total > 0 ? before / total : 1;
      const crossTime = this.lapTime !== undefined ? this.lapTime - dt * (1 - frac) : undefined;
      if (crossTime !== undefined) this.finishLap(crossTime);
      this.lapNumber++;
      this.lapTime = dt * (1 - frac);
      this.lapValid = true;
      this.sectorIndex = 0;
      this.sectorStart = 0;
      this.sectorTimes = [];
      this.car.pu.resetLap();
    } else if (crossedBackwards) {
      this.invalidate();
    } else if (this.lapTime !== undefined) {
      const end = this.layout.sectorEnds[this.sectorIndex];
      if (end !== undefined && this.prevS < end && s >= end) {
        const t = this.lapTime - this.sectorStart;
        this.sectorTimes[this.sectorIndex] = t;
        this.events.push({ kind: 'sector', sectorIndex: this.sectorIndex, sectorTime: t });
        this.sectorStart = this.lapTime;
        this.sectorIndex++;
      }
    }
    this.prevS = s;
  }

  private finishLap(time: number): void {
    const lastSector = this.layout.sectorEnds.length;
    this.sectorTimes[lastSector] = time - this.sectorStart;
    this.lastLap = time;
    if (this.lapValid) {
      if (this.bestLap === undefined || time < this.bestLap) this.bestLap = time;
      this.sectorTimes.forEach((t, i) => {
        if (t === undefined) return;
        const best = this.bestSectors[i];
        if (best === undefined || t < best) this.bestSectors[i] = t;
      });
    }
    this.events.push({ kind: 'lap', lapTime: time, valid: this.lapValid, sectors: [...this.sectorTimes] });
  }

  private nearBarrier(): boolean {
    const p = this.projection;
    const barrier = this.layout.barrierOffset(p);
    const limit = (p.offset >= 0 ? barrier.right : barrier.left) - HALF_WIDTH;
    return Math.abs(p.offset) > limit - 0.5;
  }

  private checkStuck(dt: number): void {
    const f = this.layout.track.frameAt(this.projection.s);
    const forward = Math.sin(this.car.heading) * f.tx + Math.cos(this.car.heading) * f.tz;
    const slow = this.car.speed < 3;
    const backwards = forward < -0.3;
    const offTrack = this.surface !== 'track' && this.surface !== 'kerb';
    // Lenient: stopped off track, against a wall or facing the wrong way (and not reversing out of it).
    const stuck = this.lenientReset
      ? slow && (offTrack || this.nearBarrier() || backwards)
      : (slow && offTrack) || (backwards && this.car.speed < 15) || (slow && backwards);
    if (stuck) {
      this.stuckTime += dt;
      if (this.stuckTime > (this.lenientReset ? 6 : sessionParams.resetAfter.value)) this.reset();
    } else {
      this.stuckTime = 0;
    }
  }
}

export function inZones(s: number, zones: Array<[number, number]>, length: number): boolean {
  for (const [a, b] of zones) {
    if (a <= b ? s >= a && s <= b : s >= a || s <= b % length) return true;
  }
  return false;
}
